import { Children, isValidElement } from 'react';
import { DataRowActionButton, StanDataColumn, StanDataRowChildren, StanDataRowProps } from './StanDataRow.types';
import { copyToClipboard, formatLinkDisplayText } from '@/lib/helpers';

/**
 * Handles row click events, preventing clicks on interactive elements
 */
export const handleRowClick = (e: React.MouseEvent, onClick?: (e: React.MouseEvent) => void) => {
    // Don't trigger row click if clicking on actions or other interactive elements
    if ((e.target as HTMLElement).closest('[data-no-row-click]')) {
        return;
    }

    onClick?.(e);
};

/**
 * Renders the appropriate content for a column based on its type
 * Expand this to support more column types
 */
export const renderColumnValue = (column: StanDataColumn) => {
    if (column.type === 'link') {
        const linkUrl = column.href || String(column.value || '');
        const displayText = formatLinkDisplayText(String(column.value || ''));

        return {
            type: 'link' as const,
            linkUrl,
            displayText,
            showCopyButton: column.showCopyButton,
            onCopy: column.onCopy,
        };
    }

    if (column.type === 'pill') {
        return {
            type: 'pill' as const,
            value: column.value,
            statusVariant: column.statusVariant,
        };
    }

    return {
        type: 'text' as const,
        value: column.value,
    };
};

/**
 * Generates grid template columns CSS from column widths
 */
export const generateGridTemplate = (columns: StanDataColumn[]): string => {
    return columns.map(col => (col.width ? `${col.width}%` : '1fr')).join(' ');
};

/**
 * Generates grid template for equal distribution when no widths are specified
 */
export const generateEqualGridTemplate = (columnCount: number): string => {
    return Array(columnCount).fill('1fr').join(' ');
};

/**
 * Detects if any child components have action buttons
 */
export const hasActionsInChildren = (children: StanDataRowChildren): boolean => {
    let hasActions = false;

    Children.forEach(children, child => {
        if (
            isValidElement<StanDataRowProps>(child) &&
            child.props.actionButtons &&
            child.props.actionButtons.length > 0
        ) {
            hasActions = true;
        }
    });

    return hasActions;
};

/**
 * Calculates the maximum number of action buttons across child components
 */
export const getMaxActionButtonCount = (children: StanDataRowChildren): number => {
    let maxCount = 0;

    Children.forEach(children, child => {
        if (
            isValidElement<StanDataRowProps>(child) &&
            child.props.actionButtons &&
            child.props.actionButtons.length > 0
        ) {
            maxCount = Math.max(maxCount, child.props.actionButtons.length);
        }
    });

    return maxCount;
};

/**
 * Calculates the width needed for action buttons (32px per button)
 */
export const calculateActionsWidth = (actionButtonCount: number): number => {
    const ACTION_BUTTON_WIDTH = 32;

    return actionButtonCount * ACTION_BUTTON_WIDTH;
};

/**
 * Renders an action button element based on its type
 */
export const renderActionButton = (
    actionButton: DataRowActionButton,
    onStopPropagation: (e: React.MouseEvent) => void
) => {
    const { id, onClick, type = 'button' } = actionButton;

    if (type === 'dropdown') {
        const dropdownButton = actionButton as Extract<typeof actionButton, { type: 'dropdown' }>;
        return {
            type: 'dropdown' as const,
            key: id,
            menu: dropdownButton.menu,
            onClick: onStopPropagation,
        };
    }

    // Default button type
    const buttonAction = actionButton as Extract<typeof actionButton, { type?: 'button' }>;
    return {
        key: id,
        type: 'button' as const,
        icon: buttonAction.icon,
        label: actionButton.label,
        onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onClick?.();
        },
    };
};

/**
 * Creates a notification callback for copy operations
 */
export const createCopyNotificationHandler = (stanNotify: any) => {
    return (success: boolean, error?: string) => {
        if (success) {
            stanNotify({
                type: 'success',
                title: 'Link copied!',
                duration: 2000,
            });
        } else {
            stanNotify({
                type: 'error',
                title: 'Copy failed',
                text: `Failed to copy link to clipboard: ${error || 'Unknown error'}`,
                duration: 2000,
            });
        }
    };
};

/**
 * Handles the copy action for link columns
 */
export const handleLinkCopy = async (linkUrl: string, onCopy?: (url: string) => void, stanNotify?: any) => {
    if (onCopy) {
        onCopy(linkUrl);
        return;
    }

    await copyToClipboard(linkUrl, {
        successMessage: 'Link copied!',
        errorMessage: 'Failed to copy link to clipboard',
        showNotifications: !!stanNotify,
    });
};
